import React, { useState } from 'react'
import { Layout, Space, Dropdown, Avatar, Badge, Select, Button, Modal, Form, Input, App, Descriptions, Typography, Popover } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  BellOutlined,
  UserOutlined,
  GlobalOutlined,
  QuestionCircleOutlined,
  LogoutOutlined,
  SettingOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  MailOutlined,
  EnvironmentOutlined,
  BankOutlined,
} from '@ant-design/icons'
import { api } from '@/shared/api/client'
import { useNotificationStore } from '@/stores/notificationStore'
import NotificationPanel from './NotificationPanel'
import './Header.scss'

const { Header: AntHeader } = Layout

const Header: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  const [feedbackForm] = Form.useForm()
  const { message: antMessage } = App.useApp()

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('user.profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('user.settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('user.logout'),
    },
  ]

  const helpMenuItems = [
    {
      key: 'feedback',
      icon: <MessageOutlined />,
      label: t('help.feedback'),
    },
    {
      key: 'about',
      icon: <InfoCircleOutlined />,
      label: t('help.about'),
    },
  ]

  const handleHelpMenuClick = (e: any) => {
    if (e.key === 'feedback') setFeedbackOpen(true)
    else if (e.key === 'about') setAboutOpen(true)
  }

  const handleFeedbackSubmit = async () => {
    const values = await feedbackForm.validateFields()
    setFeedbackLoading(true)
    try {
      await api.post('/suggestions', values)
      antMessage.success(t('help.feedbackModal.submitSuccess'))
      feedbackForm.resetFields()
      setFeedbackOpen(false)
    } finally {
      setFeedbackLoading(false)
    }
  }

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language)
    localStorage.setItem('language', language)
  }

  const languageOptions = [
    { value: 'zh-CN', label: t('language.zh-CN') },
    { value: 'en-US', label: t('language.en-US') },
    { value: 'pt-BR', label: t('language.pt-BR') },
  ]

  return (
    <>
      <AntHeader className="app-header">
        <div className="app-header-title" />

        <Space size="middle">
          <Select
            size="small"
            value={i18n.language}
            onChange={handleLanguageChange}
            options={languageOptions}
            style={{ width: 120 }}
            variant="borderless"
            suffixIcon={<GlobalOutlined />}
          />

          <Dropdown menu={{ items: helpMenuItems, onClick: handleHelpMenuClick }} placement="bottomRight">
            <Button type="text" icon={<QuestionCircleOutlined />} />
          </Dropdown>

          <Popover content={<NotificationPanel />} trigger="click" placement="bottomRight" arrow={false}>
            <Badge count={unreadCount} size="small">
              <Button type="text" icon={<BellOutlined />} />
            </Badge>
          </Popover>

          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar size="small" icon={<UserOutlined />} />
              <span className="app-header-username">{t('role.admin')}</span>
            </Space>
          </Dropdown>
        </Space>
      </AntHeader>

      <Modal
        title={t('help.feedbackModal.title')}
        open={feedbackOpen}
        onOk={handleFeedbackSubmit}
        onCancel={() => { setFeedbackOpen(false); feedbackForm.resetFields() }}
        confirmLoading={feedbackLoading}
        okText={t('submit')}
        cancelText={t('cancel')}
        destroyOnHidden
      >
        <Form form={feedbackForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="type" label={t('help.feedbackModal.type')} rules={[{ required: true }]}>
            <Select placeholder={t('help.feedbackModal.typePlaceholder')}>
              <Select.Option value="feature">{t('help.feedbackModal.type.feature')}</Select.Option>
              <Select.Option value="bug">{t('help.feedbackModal.type.bug')}</Select.Option>
              <Select.Option value="ui">{t('help.feedbackModal.type.ui')}</Select.Option>
              <Select.Option value="other">{t('help.feedbackModal.type.other')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="content" label={t('help.feedbackModal.content')} rules={[{ required: true }]}>
            <Input.TextArea rows={4} placeholder={t('help.feedbackModal.contentPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('help.aboutModal.title')}
        open={aboutOpen}
        onCancel={() => setAboutOpen(false)}
        footer={null}
        width={480}
      >
        <div style={{ textAlign: 'center', padding: '24px 0 16px' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff', marginBottom: 4 }}>
            {t('help.aboutModal.productName')}
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 14 }}>
            {t('help.aboutModal.version')}
          </Typography.Text>
        </div>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label={<><BankOutlined style={{ marginRight: 6 }} />{t('help.aboutModal.companyLabel')}</>}>
            {t('help.aboutModal.company')}
          </Descriptions.Item>
          <Descriptions.Item label={<><MailOutlined style={{ marginRight: 6 }} />{t('help.aboutModal.contact')}</>}>
            terry.sun@harbour-cn.com
          </Descriptions.Item>
          <Descriptions.Item label={<><EnvironmentOutlined style={{ marginRight: 6 }} />{t('help.aboutModal.address')}</>}>
            {t('help.aboutModal.addressValue')}
          </Descriptions.Item>
        </Descriptions>
        <div style={{ textAlign: 'center', color: '#999', fontSize: 12 }}>
          {t('help.aboutModal.copyright')}
        </div>
      </Modal>
    </>
  )
}

export default Header
